/* Dutch & Dutch — theme.js
 * Minimal vanilla JS for header, accordion, carousel, swatches, reveal. */
(function () {
  'use strict';

  const $  = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Money ---------- */
  // Formats an amount in cents using a Shopify money_format string, handling all
  // placeholder variants (e.g. {{amount}}, {{amount_no_decimals}},
  // {{amount_with_comma_separator}}, …) so custom store formats render correctly.
  function formatMoney(cents, format) {
    const formatString = format || '€{{amount}}';
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;

    const withDelimiters = (number, precision, thousands, decimal) => {
      if (number == null || isNaN(number)) return '0';
      const fixed = (Number(number) / 100).toFixed(precision);
      const parts = fixed.split('.');
      const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const frac = parts[1] ? decimal + parts[1] : '';
      return whole + frac;
    };

    const match = formatString.match(placeholder);
    let value = withDelimiters(cents, 2, ',', '.');
    if (match) {
      switch (match[1]) {
        case 'amount': value = withDelimiters(cents, 2, ',', '.'); break;
        case 'amount_no_decimals': value = withDelimiters(cents, 0, ',', '.'); break;
        case 'amount_with_comma_separator': value = withDelimiters(cents, 2, '.', ','); break;
        case 'amount_no_decimals_with_comma_separator': value = withDelimiters(cents, 0, '.', ','); break;
        case 'amount_with_apostrophe_separator': value = withDelimiters(cents, 2, "'", '.'); break;
        case 'amount_no_decimals_with_apostrophe_separator': value = withDelimiters(cents, 0, "'", '.'); break;
        case 'amount_with_space_separator': value = withDelimiters(cents, 2, ' ', ','); break;
        case 'amount_no_decimals_with_space_separator': value = withDelimiters(cents, 0, ' ', ','); break;
      }
    }
    return formatString.replace(placeholder, value);
  }

  /* ---------- Accordion ---------- */
  function initAccordion(root) {
    $$('.acc-item', root).forEach((item) => {
      const trigger = $('.acc-trigger', item);
      const panel = $('.acc-panel', item);
      if (!trigger || !panel) return;

      const setOpen = (open) => {
        item.dataset.state = open ? 'open' : 'closed';
        panel.style.maxHeight = open ? panel.scrollHeight + 'px' : '0px';
        trigger.setAttribute('aria-expanded', String(open));
      };

      trigger.setAttribute('aria-expanded', item.dataset.state === 'open');
      if (item.dataset.state === 'open') setOpen(true);

      trigger.addEventListener('click', () => {
        const isOpen = item.dataset.state === 'open';
        setOpen(!isOpen);
      });
    });
  }

  /* ---------- Finish picker (renders image swap) ---------- */
  function initFinishPicker(root) {
    $$('.finish-picker', root).forEach((picker) => {
      const targetId = picker.dataset.target;
      const nameEl = $('#' + picker.dataset.nameOutput, root);
      const swatches = $$('.swatch', picker);
      const target = targetId ? document.getElementById(targetId) : null;

      swatches.forEach((sw) => {
        sw.addEventListener('click', () => {
          swatches.forEach((s) => s.setAttribute('aria-pressed', 'false'));
          sw.setAttribute('aria-pressed', 'true');
          if (target && sw.dataset.image) {
            target.src = sw.dataset.image;
            target.alt = sw.dataset.alt || target.alt;
          }
          if (nameEl && sw.dataset.finishName) nameEl.textContent = sw.dataset.finishName;
        });
      });
    });
  }

  /* ---------- Carousel (3-up cards, mobile swipe) ---------- */
  function initCarousel(root) {
    $$('[data-carousel]', root).forEach((wrap) => {
      const track = $('.cards', wrap);
      const dots = $$('.dot', wrap);
      const prev = $('.cbtn[data-dir="prev"]', wrap);
      const next = $('.cbtn[data-dir="next"]', wrap);
      if (!track) return;

      let index = 0;
      const items = $$('.media-card', track);
      const cnav = $('.cnav', wrap);
      const dotsWrap = $('.dots', wrap);
      const perView = () => (innerWidth < 760 ? 1 : innerWidth < 1024 ? 2 : 3);

      // Only expose the prev/next controls and dots when the cards actually
      // overflow the view (i.e. there is something to scroll to).
      const setControls = (active) => {
        if (cnav) cnav.hidden = !active;
        if (dotsWrap) dotsWrap.hidden = !active;
      };

      const go = (i) => {
        const max = Math.max(0, items.length - perView());
        index = Math.max(0, Math.min(i, max));
        const card = items[0];
        if (!card) return;
        const styles = getComputedStyle(track);
        const gap = parseFloat(styles.columnGap || styles.gap || '0');
        const w = card.getBoundingClientRect().width + gap;
        track.style.transform = `translateX(${-w * index}px)`;
        dots.forEach((d, di) => d.classList.toggle('on', di === index));
      };

      // Only enable transform-based slider on mobile/tablet
      const enable = () => {
        const active = perView() < items.length;
        setControls(active);
        if (active) {
          track.style.transition = 'transform .45s cubic-bezier(.2,.7,.2,1)';
          go(index);
        } else {
          index = 0;
          track.style.transition = '';
          track.style.transform = '';
        }
      };

      enable();
      addEventListener('resize', enable);
      prev?.addEventListener('click', () => go(index - 1));
      next?.addEventListener('click', () => go(index + 1));
      dots.forEach((d, di) => d.addEventListener('click', () => go(di)));
    });
  }

  /* ---------- Mobile nav ---------- */
  function initMobileNav(root) {
    const toggle = $('.nav-toggle', root);
    const drawer = $('.mobile-nav', root);
    const close = $('.mobile-nav .close', root);
    if (!toggle || !drawer) return;
    const open = () => { drawer.dataset.open = 'true'; document.body.style.overflow = 'hidden'; };
    const shut = () => { drawer.dataset.open = 'false'; document.body.style.overflow = ''; };
    toggle.addEventListener('click', open);
    close?.addEventListener('click', shut);
    drawer.addEventListener('click', (e) => {
      if (e.target.tagName === 'A') shut();
    });
  }

  /* ---------- Reveal on scroll ---------- */
  function initReveal() {
    if (reduceMotion || !('IntersectionObserver' in window)) {
      $$('.reveal').forEach((el) => el.classList.add('is-in'));
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            obs.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
    );
    $$('.reveal').forEach((el) => obs.observe(el));
  }

  /* ---------- PDP (commerce product page) ---------- */
  function initPdp(root) {
    $$('[data-pdp]', root).forEach((pdp) => {
      const dataEl = $('[data-pdp-product-json]', pdp);
      if (!dataEl) return;
      let productData;
      try {
        productData = JSON.parse(dataEl.textContent);
      } catch (err) {
        console.warn('[pdp] could not parse product JSON', err);
        return;
      }

      const variants = productData.variants || [];
      const swatchGroups = $$('[data-pdp-option]', pdp);
      const variantInput = $('[data-pdp-variant-id]', pdp);
      const submit = $('[data-pdp-submit]', pdp);
      const priceEl = $('[data-pdp-price]', pdp);
      const compareEl = $('[data-pdp-compare]', pdp);
      const mainImg = $('.pdp-gallery-main img', pdp);
      const thumbs = $$('.pdp-gallery-thumb', pdp);

      const moneyFormat = pdp.dataset.moneyFormat || '€{{amount}}';
      const money = (cents) => formatMoney(cents, moneyFormat);

      const findVariant = () => {
        const selected = swatchGroups.map((group) => {
          const active = group.querySelector('[aria-pressed="true"]');
          return active ? active.dataset.value : null;
        });
        return variants.find((v) => {
          return selected.every((val, i) => {
            if (val == null) return true;
            return v.options[i] === val;
          });
        });
      };

      const render = (variant) => {
        if (!variant) return;
        if (variantInput) variantInput.value = variant.id;
        if (priceEl) priceEl.textContent = money(variant.price);
        if (compareEl) {
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            compareEl.textContent = money(variant.compare_at_price);
            compareEl.hidden = false;
          } else {
            compareEl.hidden = true;
          }
        }
        if (submit) {
          submit.disabled = !variant.available;
          submit.textContent = variant.available ? (submit.dataset.labelAvailable || 'Add to cart') : (submit.dataset.labelSoldOut || 'Sold out');
        }
        if (mainImg && variant.featured_image && variant.featured_image.src) {
          mainImg.src = variant.featured_image.src;
          if (variant.featured_image.alt) mainImg.alt = variant.featured_image.alt;
        }
        if (variant.featured_image && variant.featured_image.id) {
          thumbs.forEach((t) => {
            const matches = String(t.dataset.imageId) === String(variant.featured_image.id);
            t.setAttribute('aria-current', matches ? 'true' : 'false');
          });
        }
        const labelOutputs = $$('[data-pdp-option-output]', pdp);
        labelOutputs.forEach((out, i) => {
          if (variant.options[i] != null) out.textContent = variant.options[i];
        });
      };

      swatchGroups.forEach((group) => {
        $$('[data-pdp-swatch]', group).forEach((sw) => {
          sw.addEventListener('click', () => {
            $$('[data-pdp-swatch]', group).forEach((s) => s.setAttribute('aria-pressed', 'false'));
            sw.setAttribute('aria-pressed', 'true');
            render(findVariant());
          });
        });
      });

      thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => {
          if (!mainImg) return;
          const img = $('img', thumb);
          if (!img) return;
          // The main image has a srcset/sizes, which take precedence over src.
          // Clear them so the newly chosen image actually renders.
          mainImg.removeAttribute('srcset');
          mainImg.removeAttribute('sizes');
          mainImg.src = thumb.dataset.fullSrc || img.currentSrc || img.src;
          mainImg.alt = img.alt;
          thumbs.forEach((t) => t.setAttribute('aria-current', t === thumb ? 'true' : 'false'));
        });
      });

      render(findVariant());
    });
  }

  /* ---------- Boot ---------- */
  function boot(root = document) {
    initAccordion(root);
    initFinishPicker(root);
    initCarousel(root);
    initMobileNav(root);
    initReveal(root);
    initPdp(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot());
  } else {
    boot();
  }

  // Re-init individual sections when Shopify theme editor reloads them
  document.addEventListener('shopify:section:load', (e) => boot(e.target));
})();
